import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  findPdfsByUser,
  findPdfById,
  findPdfFields,
  getSignedUrl,
  deletePdf,
  renamePdf,
  updateAutoFullscreen,
  updatePdfFolder,
  setShareSettings,
  type PdfFileRow,
} from "../db/pdfRepository";

export function usePdfList(userId: string | null) {
  return useQuery<PdfFileRow[]>({
    queryKey: ["pdfs", userId],
    queryFn: () => findPdfsByUser(userId!),
    enabled: !!userId,
  });
}

export function usePdf(pdfId: string | null) {
  return useQuery<PdfFileRow>({
    queryKey: ["pdfs", pdfId],
    queryFn: () => findPdfById(pdfId!),
    enabled: !!pdfId,
  });
}

export function usePdfFields<T = Record<string, unknown>>(
  pdfId: string | null,
  fields: string,
) {
  return useQuery<T | null>({
    queryKey: ["pdfs", pdfId, fields],
    queryFn: () => findPdfFields<T>(pdfId!, fields),
    enabled: !!pdfId,
  });
}

export function usePdfSignedUrl(pdfId: string | null, filePath: string | null) {
  return useQuery<string>({
    queryKey: ["pdfs", pdfId, "signedUrl"],
    queryFn: () => getSignedUrl(filePath!),
    enabled: !!pdfId && !!filePath,
    staleTime: 50 * 60 * 1000, // 50분 (서명 URL 1시간 만료)
  });
}

export function useDeletePdf() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      pdfId,
      filePath,
      thumbnailUrl,
    }: {
      pdfId: string;
      filePath: string;
      thumbnailUrl: string | null;
    }) => deletePdf(pdfId, filePath, thumbnailUrl),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["pdfs"] });
    },
  });
}

export function useRenamePdf() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      pdfId,
      newFileName,
    }: {
      pdfId: string;
      newFileName: string;
    }) => renamePdf(pdfId, newFileName),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["pdfs"] });
    },
  });
}

export function useToggleAutoFullscreen() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      pdfId,
      userId,
      enabled,
    }: {
      pdfId: string;
      userId: string;
      enabled: boolean;
    }) => updateAutoFullscreen(pdfId, userId, enabled),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["pdfs"] });
    },
  });
}

export function useUpdatePdfFolder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      pdfId,
      folderId,
    }: {
      pdfId: string;
      folderId: string | null;
    }) => updatePdfFolder(pdfId, folderId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["pdfs"] });
    },
  });
}

export function useSaveShareSettings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: setShareSettings,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["pdfs", variables.pdfId],
      });
    },
  });
}
